import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Plus, Trash2, Edit2, RefreshCw, Pill, History, 
  CheckCircle, X, AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';

const API = '/api';

function MedicationCard({ med, onEdit, onDelete, onToggleActive, onViewHistory }) {
  const [showHistory, setShowHistory] = useState(false);
  const hasHistory = (med.strength_history || []).length > 0;
  
  return (
    <Card className={`bg-adm-surface/[0.02] border-white/[0.06] ${!med.is_active ? 'opacity-40' : ''}`} data-testid={`medication-card-${med.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Pill className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <div className="text-sm font-medium text-white/90">{med.medicine_name}</div>
              <div className="text-xs text-white/40 font-mono">{med.code}</div>
            </div>
          </div>
          <Badge className="bg-amber-500/10 text-amber-400 border-0 text-[9px]">
            {med.strength} {med.unit}
          </Badge>
        </div>
        
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-white/30">Generic:</span>
            <span className="text-white/60 ml-1">{med.generic_name || '—'}</span>
          </div>
          <div>
            <span className="text-white/30">Form:</span>
            <span className="text-white/60 ml-1">{med.form || '—'}</span>
          </div>
          {med.brand && (
            <div className="col-span-2">
              <span className="text-white/30">Brand:</span>
              <span className="text-white/60 ml-1">{med.brand}</span>
            </div>
          )}
        </div>
        
        {med.notes && (
          <div className="mt-2 text-xs text-white/40 italic">{med.notes}</div>
        )}
        
        {med.interaction_flags && Object.keys(med.interaction_flags).length > 0 && (
          <div className="mt-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] text-amber-400">Has interaction flags</span>
          </div>
        )}
        
        {/* Strength History */}
        {hasHistory && (
          <div className="mt-3 border-t border-white/[0.06] pt-3">
            <button 
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-1 text-[10px] text-white/40 hover:text-white/60"
            >
              <History className="w-3 h-3" />
              Strength History ({med.strength_history.length})
              {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showHistory && (
              <div className="mt-2 space-y-1">
                {med.strength_history.map((h, i) => (
                  <div key={i} className="text-[10px] text-white/40 flex items-center gap-2">
                    <span className="text-white/30">{new Date(h.changed_at).toLocaleDateString()}</span>
                    <span className="text-red-400/60">{h.old_strength}</span>
                    <span>→</span>
                    <span className="text-emerald-400/60">{h.new_strength}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        <div className="mt-3 flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => onEdit(med)} className="h-7 px-2 text-[10px] text-white/40 hover:text-white">
            <Edit2 className="w-3 h-3 mr-1" /> Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onToggleActive(med)} className="h-7 px-2 text-[10px] text-white/40 hover:text-amber-400">
            {med.is_active ? <X className="w-3 h-3 mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
            {med.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(med.id)} className="h-7 px-2 text-[10px] text-white/40 hover:text-red-400">
            <Trash2 className="w-3 h-3 mr-1" /> Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MedicationEditDialog({ med, open, onClose, onSave }) {
  const [form, setForm] = useState(med || {});
  
  useEffect(() => {
    if (med) setForm(med);
    else setForm({ code: '', medicine_name: '', generic_name: '', strength: '', unit: '', form: '', brand: '', notes: '', interaction_flags: {} });
  }, [med]);
  
  const handleSave = () => {
    onSave(form);
    onClose();
  };
  
  if (!open) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>{med?.id ? 'Edit Medication Definition' : 'Add Medication Definition'}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-white/40 mb-1 block">Code *</label>
              <Input value={form.code || ''} onChange={e => setForm({...form, code: e.target.value})} 
                className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="MED-001" />
            </div>
            <div>
              <label className="text-[10px] text-white/40 mb-1 block">Medicine Name *</label>
              <Input value={form.medicine_name || ''} onChange={e => setForm({...form, medicine_name: e.target.value})} 
                className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="Levodopa" />
            </div>
          </div>
          
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Generic Name</label>
            <Input value={form.generic_name || ''} onChange={e => setForm({...form, generic_name: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="Levodopa/Carbidopa" />
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-white/40 mb-1 block">Strength</label>
              <Input value={form.strength || ''} onChange={e => setForm({...form, strength: e.target.value})} 
                className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="100/25" />
            </div>
            <div>
              <label className="text-[10px] text-white/40 mb-1 block">Unit</label>
              <Input value={form.unit || ''} onChange={e => setForm({...form, unit: e.target.value})} 
                className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="mg" />
            </div>
            <div>
              <label className="text-[10px] text-white/40 mb-1 block">Form</label>
              <Input value={form.form || ''} onChange={e => setForm({...form, form: e.target.value})} 
                className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="tablet" />
            </div>
          </div>
          
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Brand</label>
            <Input value={form.brand || ''} onChange={e => setForm({...form, brand: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="Sinemet" />
          </div>
          
          <div>
            <label className="text-[10px] text-white/40 mb-1 block">Notes</label>
            <Input value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} 
              className="bg-adm-surface/5 border-white/10 text-white h-8 text-xs" placeholder="Take with food to reduce nausea" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-white/50">Cancel</Button>
          <Button onClick={handleSave} disabled={!form.code || !form.medicine_name} className="bg-amber-600 hover:bg-amber-700 text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MedicationDefinitions() {
  const { user } = useAuth();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMed, setEditMed] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  const token = localStorage.getItem('rf_access_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  
  const fetchDefinitions = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/v3/medication-definitions`, { headers });
    if (res.ok) {
      const data = await res.json();
      setDefinitions(data.definitions || []);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);
  
  const handleAdd = () => {
    setEditMed(null);
    setShowEditDialog(true);
  };
  
  const handleEdit = (med) => {
    setEditMed(med);
    setShowEditDialog(true);
  };
  
  const handleSave = async (formData) => {
    if (formData.id) {
      await fetch(`${API}/admin/v3/medication-definitions/${formData.id}`, {
        method: 'PUT', headers, body: JSON.stringify(formData)
      });
    } else {
      await fetch(`${API}/admin/v3/medication-definitions`, {
        method: 'POST', headers, body: JSON.stringify(formData)
      });
    }
    fetchDefinitions();
  };
  
  const handleToggleActive = async (med) => {
    // For toggle, we just need to update is_active
    if (med.is_active) {
      await fetch(`${API}/admin/v3/medication-definitions/${med.id}`, {
        method: 'DELETE', headers
      });
    } else {
      // Reactivate by updating
      await fetch(`${API}/admin/v3/medication-definitions/${med.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ ...med, is_active: true })
      });
    }
    fetchDefinitions();
  };
  
  const handleDelete = async (medId) => {
    if (!confirm('Delete this medication definition?')) return;
    await fetch(`${API}/admin/v3/medication-definitions/${medId}`, {
      method: 'DELETE', headers
    });
    fetchDefinitions();
  };
  
  // Group by generic name
  const grouped = definitions.reduce((acc, med) => {
    const key = med.generic_name || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(med);
    return acc;
  }, {});
  
  const activeMeds = definitions.filter(m => m.is_active !== false);
  const withHistory = definitions.filter(m => (m.strength_history || []).length > 0);
  
  return (
    <AdminLayout>
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="medication-definitions">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-white/90">Medication Definitions</h1>
            <p className="text-xs text-white/40 mt-1">Preserve drug composition, strength, and track changes</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={fetchDefinitions} size="sm" variant="ghost" className="text-xs text-white/50 h-8">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
            <Button onClick={handleAdd} size="sm" className="text-xs bg-amber-600 hover:bg-amber-700 text-white h-8">
              <Plus className="w-3 h-3 mr-1" /> Add Medication
            </Button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <Card className="bg-adm-surface/[0.02] border-white/[0.06]">
            <CardContent className="p-3 flex items-center gap-3">
              <Pill className="w-8 h-8 text-amber-400" />
              <div>
                <div className="text-2xl font-semibold text-white/90">{definitions.length}</div>
                <div className="text-[10px] text-white/40">Total Medications</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-adm-surface/[0.02] border-white/[0.06]">
            <CardContent className="p-3 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
              <div>
                <div className="text-2xl font-semibold text-white/90">{activeMeds.length}</div>
                <div className="text-[10px] text-white/40">Active</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-adm-surface/[0.02] border-white/[0.06]">
            <CardContent className="p-3 flex items-center gap-3">
              <History className="w-8 h-8 text-blue-400" />
              <div>
                <div className="text-2xl font-semibold text-white/90">{withHistory.length}</div>
                <div className="text-[10px] text-white/40">With Strength History</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-adm-surface/[0.02] border-white/[0.06]">
            <CardContent className="p-3 flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-rose-400" />
              <div>
                <div className="text-2xl font-semibold text-white/90">{definitions.filter(m => Object.keys(m.interaction_flags || {}).length > 0).length}</div>
                <div className="text-[10px] text-white/40">With Interactions</div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64 text-white/40">Loading...</div>
        ) : definitions.length === 0 ? (
          <div className="text-center py-12 text-white/40">
            <Pill className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>No medication definitions found</p>
            <Button onClick={handleAdd} className="mt-4 bg-amber-600 hover:bg-amber-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Add First Medication
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([genericName, meds]) => (
              <div key={genericName}>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-sm font-medium text-white/70">{genericName}</h2>
                  <Badge className="bg-adm-surface/5 text-white/40 border-0 text-[9px]">{meds.length}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {meds.map(med => (
                    <MedicationCard 
                      key={med.id} 
                      med={med} 
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleActive={handleToggleActive}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Edit Dialog */}
        <MedicationEditDialog 
          med={editMed} 
          open={showEditDialog} 
          onClose={() => setShowEditDialog(false)}
          onSave={handleSave}
        />
      </div>
    </AdminLayout>
  );
}
